import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from '../lib/i18n';
import { useTheme } from '../lib/theme';
import { Button } from '../components/ui/button';
import { Shield, ArrowLeft, Sun, Moon, CheckCircle } from 'lucide-react';
import api from '../lib/api';

export default function ResetPassword() {
  const { t, language, changeLanguage } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(language === 'fr' ? 'Les mots de passe ne correspondent pas' : 'Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError(language === 'fr' ? 'Le mot de passe doit contenir au moins 8 caractères' : 'Password must be at least 8 characters');
      return;
    }

    setLoading(true);

    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-white dark:bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-heading text-2xl text-slate-900 dark:text-white mb-2">Invalid Reset Link</h2>
          <p className="text-slate-600 dark:text-neutral-400 text-sm mb-4">This password reset link is invalid or has expired.</p>
          <Link to="/forgot-password">
            <Button>Request New Link</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-[#050505] text-slate-900 dark:text-white flex">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-gradient-to-br from-blue-600 to-purple-600 p-12 flex-col justify-between">
        <div className="flex items-center gap-2">
          <Shield className="w-6 h-6 text-white" strokeWidth={1.5} />
          <span className="text-white font-heading text-xl tracking-tight">SheetArmor</span>
        </div>
        <div>
          <h2 className="font-heading text-4xl text-white mb-4">{t('auth.left_panel_title')}</h2>
          <p className="text-white/80 text-lg font-body leading-relaxed">{t('auth.left_panel_desc')}</p>
        </div>
      </div>

      {/* Right Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-8 flex items-center justify-between">
            <Link to="/login" className="flex items-center gap-2 text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white transition-colors">
              <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-sm font-body">Back to Login</span>
            </Link>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={toggleTheme}
                className="text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white h-8 w-8 p-0"
              >
                {theme === 'dark' ? <Sun className="w-4 h-4" strokeWidth={1.5} /> : <Moon className="w-4 h-4" strokeWidth={1.5} />}
              </Button>
              <button
                onClick={() => changeLanguage(language === 'en' ? 'fr' : 'en')}
                className="text-xs font-body text-slate-600 hover:text-slate-900 dark:text-neutral-400 dark:hover:text-white transition-colors"
              >
                {language === 'en' ? 'FR' : 'EN'}
              </button>
            </div>
          </div>

          <div className="lg:hidden flex items-center gap-2 mb-8">
            <Shield className="w-5 h-5 text-blue-600" strokeWidth={1.5} />
            <span className="font-heading text-xl tracking-tight">SheetArmor</span>
          </div>

          {!success ? (
            <>
              <h1 className="font-heading text-3xl tracking-tight mb-2">
                {language === 'fr' ? 'Nouveau mot de passe' : 'Reset Password'}
              </h1>
              <p className="text-slate-600 dark:text-neutral-400 text-sm font-body mb-8">
                {language === 'fr'
                  ? 'Entrez votre nouveau mot de passe ci-dessous.'
                  : 'Enter your new password below.'}
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="bg-red-600/10 border border-red-600/30 rounded-sm p-3 text-red-600 text-sm">
                    {error}
                  </div>
                )}

                <div>
                  <label className="text-xs font-body text-slate-600 dark:text-neutral-400 uppercase tracking-wider mb-2 block">
                    {language === 'fr' ? 'Nouveau mot de passe' : 'New Password'}
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-[#0A0A0A] border border-slate-200 dark:border-white/10 rounded-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-neutral-600 focus:outline-none focus:ring-2 focus:ring-blue-600/50 font-body text-sm"
                    placeholder={language === 'fr' ? 'Au moins 8 caractères' : 'At least 8 characters'}
                    required
                    minLength={8}
                  />
                </div>

                <div>
                  <label className="text-xs font-body text-slate-600 dark:text-neutral-400 uppercase tracking-wider mb-2 block">
                    {language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm Password'}
                  </label>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-[#0A0A0A] border border-slate-200 dark:border-white/10 rounded-sm text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-neutral-600 focus:outline-none focus:ring-2 focus:ring-blue-600/50 font-body text-sm"
                    placeholder={language === 'fr' ? 'Confirmer le mot de passe' : 'Confirm password'}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-slate-900 text-white hover:bg-slate-800 dark:bg-white dark:text-black dark:hover:bg-neutral-200 h-11 text-xs font-body tracking-widest rounded-sm"
                >
                  {loading
                    ? (language === 'fr' ? 'MISE À JOUR...' : 'UPDATING...')
                    : (language === 'fr' ? 'RÉINITIALISER LE MOT DE PASSE' : 'RESET PASSWORD')}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center">
              <div className="w-12 h-12 bg-green-600/10 border border-green-600/30 rounded-sm flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" strokeWidth={1.5} />
              </div>
              <h2 className="font-heading text-2xl tracking-tight mb-2">
                {language === 'fr' ? 'Mot de passe réinitialisé' : 'Password Reset'}
              </h2>
              <p className="text-slate-600 dark:text-neutral-400 text-sm font-body mb-6">
                {language === 'fr'
                  ? 'Votre mot de passe a été réinitialisé avec succès. Redirection vers la connexion...'
                  : 'Your password has been reset successfully. Redirecting to login...'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
